// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Asset icon selector component that displays predefined MUI icons in categories
 * and allows users to select their organization's default asset icon.
 */
import { MARKER_CATEGORIES } from '@/account/constants/asset-marker-categories';
import {
  Box,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

interface Props {
  /**
   * Currently selected marker name.
   */
  selectedMarker: string | null;

  /**
   * Callback when a marker is selected.
   */
  onMarkerSelect: (markerName: string) => void;
}

/**
 * Displays categorized icon options for asset marker selection.
 */
export function AssetMarkerSelector(props: Props) {
  const theme = useTheme();

  return (
    <Stack spacing={2}>
      {Object.entries(MARKER_CATEGORIES).map(([categoryName, icons]) => (
        <Box key={categoryName}>
          <Stack alignItems="left" gap={1}>
            <Typography variant="subtitle2" color="text.secondary">
              {categoryName}
            </Typography>
            <Grid container spacing={1}>
              {Object.entries(icons).map(([iconName, IconComponent]) => (
                <Grid item key={iconName}>
                  <IconButton
                    onClick={() => props.onMarkerSelect(iconName)}
                    sx={{
                      width: 48,
                      height: 48,
                      border: '2px solid',
                      borderColor:
                        props.selectedMarker === iconName
                          ? theme.palette.primary.main
                          : theme.palette.divider,
                      backgroundColor:
                        props.selectedMarker === iconName
                          ? theme.palette.primary.light + '20'
                          : 'white',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor:
                          props.selectedMarker === iconName
                            ? theme.palette.primary.light + '20'
                            : theme.palette.primary.light + '10',
                        transform: 'scale(1.08)',
                        transition: 'all 0.1s ease',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      },
                    }}
                  >
                    <IconComponent
                      fontSize="small"
                      sx={{
                        color:
                          props.selectedMarker === iconName
                            ? theme.palette.primary.main
                            : 'inherit',
                      }}
                    />
                  </IconButton>
                </Grid>
              ))}
            </Grid>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
